import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import { showToast } from '@/components/Layout';
import { Button, Callout, FormCheckbox, Modal } from '@/components/UI';
import { useAppQuery } from '@/hooks/useAppQuery';
import type { EntryOptimizerStudy, EntryOptimizerStudyVariantResult } from '../entryOptimizerUtils';

/**
 * Variant promotion (§9.6): the one case where the published revision's entry
 * SEMANTICS change wholesale — the variant diff renders first, above any tuned
 * threshold/gate values. Attaching requires a PASSED confirmation run on the
 * variant's winning candidate, with no acknowledgment override.
 */
const PromoteVariantModal = ({
    study,
    variant,
    onClose
}: {
    study: EntryOptimizerStudy | null;
    variant: EntryOptimizerStudyVariantResult | null;
    onClose: () => void;
}) => {
    const [attachToStrategy, setAttachToStrategy] = useState(false);
    const [acknowledgeUnvalidatedPublish, setAcknowledgeUnvalidatedPublish] = useState(false);

    const confirmationsQuery = useAppQuery({
        ...trpc.entryOptimizer.listConfirmations.queryOptions({ jobId: variant?.jobId ?? 0 }),
        enabled: variant !== null && variant.jobId !== null
    });

    const hasPassedConfirmation = useMemo(
        () =>
            (confirmationsQuery.data?.confirmations ?? []).some(
                (confirmation) => confirmation.candidateId === variant?.bestCandidateId && confirmation.parityVerdict === 'passed'
            ),
        [confirmationsQuery.data?.confirmations, variant?.bestCandidateId]
    );

    const promoteMutation = useMutation({
        ...trpc.entryOptimizer.promoteStudyVariant.mutationOptions(),
        onError: (error) => showToast(error.message, 'error'),
        onSuccess: (result) => {
            const attached = result.promotion.attachedStrategyId !== null;

            showToast(
                `Published definition ${result.promotion.definitionId} revision ${result.promotion.revision} ` +
                    `(${result.promotion.variantSummary})${attached ? ` and attached to strategy #${result.promotion.attachedStrategyId}` : ''}`,
                'success',
                true
            );

            onClose();
        }
    });

    // The leaderboard only offers promotion for gate-passing best candidates, but
    // keep the ack flow for completeness (the API enforces it regardless).
    const attachBlocked = attachToStrategy && !hasPassedConfirmation;

    const canSubmit = study !== null && variant?.bestCandidateId != null && !promoteMutation.isPending && !attachBlocked;

    return (
        <Modal isOpen={variant !== null} title="Advanced: publish source entry" onClose={onClose}>
            <div className="flex flex-col gap-4">
                {/* The variant diff leads: this is a wholesale system change. */}
                <div className="rounded-md border border-amber-900/60 bg-amber-950/20 p-3">
                    <div className="text-sm font-medium text-amber-200">Entry system change</div>
                    <div className="mt-1 text-sm text-zinc-300">{variant?.summary}</div>
                    <div className="mt-1 text-xs text-zinc-500">
                        Every occurrence of the swapped level keys moves together (selectors, filters and captures); the variant&apos;s
                        tuned thresholds/gates are applied on top.
                    </div>
                </div>

                <p className="text-sm text-zinc-400">
                    Publishing creates the next revision of the strategy&apos;s source entry definition with the swapped levels plus
                    candidate #{variant?.bestCandidateId}&apos;s tuned values. Without attaching, the strategy keeps its current entry.
                </p>

                <FormCheckbox
                    checked={attachToStrategy}
                    label="Also attach the new revision to the strategy (replaces its current entry version)"
                    onChange={(event) => setAttachToStrategy(event.target.checked)}
                />

                {attachToStrategy && hasPassedConfirmation && (
                    <Callout variant="success">
                        A passed confirmation run backs this variant&apos;s candidate — attach is validated.
                    </Callout>
                )}

                {attachBlocked && (
                    <Callout variant="error">
                        Attaching a variant changes the entry&apos;s semantics wholesale — it requires a PASSED confirmation run and cannot
                        be acknowledged away. Run one from the variant&apos;s{' '}
                        {variant?.jobId !== null ? (
                            <Link className="text-cyan-400 hover:underline" to={`/user/entryOptimizer/${variant?.jobId}`}>
                                sweep job page
                            </Link>
                        ) : (
                            'sweep job page'
                        )}{' '}
                        first.
                    </Callout>
                )}

                <FormCheckbox
                    checked={acknowledgeUnvalidatedPublish}
                    label="Acknowledge publish if the candidate misses validation gates (rarely needed: the leaderboard surfaces gate-passers)"
                    onChange={(event) => setAcknowledgeUnvalidatedPublish(event.target.checked)}
                />

                <div className="flex justify-end gap-2">
                    <Button variant="outline-dark" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        disabled={!canSubmit}
                        onClick={() => {
                            if (!study || variant?.bestCandidateId == null) return;

                            promoteMutation.mutate({
                                studyId: study.id,
                                variantKey: variant.variantKey,
                                candidateId: variant.bestCandidateId,
                                attachToStrategy,
                                ...(acknowledgeUnvalidatedPublish ? { acknowledgeUnvalidatedPublish } : {})
                            });
                        }}
                    >
                        {promoteMutation.isPending ? 'Publishing…' : attachToStrategy ? 'Publish & Attach' : 'Publish Revision'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PromoteVariantModal;
